import React, { useState } from 'react';
import {
  faSearch,
  faFileAlt,
  faCalendar,
  faUser,
  faMapMarkerAlt,
  faBox,
  faImage,
  faSpinner,
  faTimesCircle,
  faCheck,
  faTruck,
  faHourglassHalf,
  faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import BASE_URL from '../../api';

const TRACKING_API = `${BASE_URL}/api/onlinebookings/track`;

const Tracking = () => {
  const [docketNumber, setDocketNumber] = useState('');
  const [trackingData, setTrackingData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const token = () => localStorage.getItem('token');

  const fetchTracking = async () => {
    if (!docketNumber.trim()) {
      setError('Please enter a docket number');
      return;
    }

    setLoading(true);
    setError(null);
    setTrackingData(null);

    const t = token();
    if (!t) {
      setError('Login required');
      setLoading(false);
      return;
    }

    const params = new URLSearchParams();
    params.append('docketnumber', docketNumber.trim());

    try {
      const res = await fetch(TRACKING_API, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${t}`,
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: params,
      });

      // Check the Content-Type header to see if it's actually JSON
      const contentType = res.headers.get("content-type");

      if (contentType && contentType.indexOf("application/json") !== -1) {
        const json = await res.json();

        if (json.success && json.data) {
          setTrackingData(json.data);
        } else {
          // This shows your custom message if the API says "success: false"
          setError(json.message || 'Invalid docket number or no data found');
        }
      } else {
        // If the server returns HTML (the <!DOCTYPE error), show your friendly message
        setError('Invalid docket number or no data found');
      }

    } catch (e) {
      // This catches network failures or total crashes
      setError('Connection error. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Status Badge with Icon
  const getStatusStyle = (status) => {
    const s = (status || '').toString().toLowerCase();
    let bg = 'bg-gray-100 text-gray-700';
    let icon = faExclamationTriangle;
    let iconColor = 'text-gray-600';

    if (s.includes('delivered') || s.includes('pod uploaded')) {
      bg = 'bg-green-100 text-green-800';
      icon = faCheck;
      iconColor = 'text-green-600';
    } else if (s.includes('transit') || s.includes('in progress')) {
      bg = 'bg-blue-100 text-blue-800';
      icon = faTruck;
      iconColor = 'text-blue-600';
    } else if (s.includes('pending') || s.includes('hold')) {
      bg = 'bg-yellow-100 text-yellow-800';
      icon = faHourglassHalf;
      iconColor = 'text-yellow-600';
    }

    return { bg, icon, iconColor };
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-orange-50 p-6 lg:p-10">
      <div className="max-w-4xl mx-auto">

        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-3xl font-bold text-gray-800 flex items-center justify-center gap-3">
            <FontAwesomeIcon icon={faTruck} className="text-orange-600" />
            Track Shipment
          </h1>
          <p className="text-gray-600 mt-2">Enter docket number to view live status</p>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl flex items-center gap-2">
            <FontAwesomeIcon icon={faTimesCircle} />
            {error}
          </div>
        )}

        {/* Search Box */}
        <div className="bg-white rounded-3xl shadow-xl p-8 border border-gray-100">
          <div className="flex flex-col sm:flex-row gap-4">
            <input
              type="text"
              value={docketNumber}
              onChange={(e) => setDocketNumber(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && fetchTracking()}
              placeholder="e.g., DC000014"
              className="flex-1 px-5 py-4 border border-gray-300 rounded-2xl focus:outline-none focus:ring-4 focus:ring-orange-200 focus:border-orange-500 transition-all text-lg font-medium"
              disabled={loading}
            />
            <button
              onClick={fetchTracking}
              disabled={loading}
              className="px-8 py-4 bg-gradient-to-r from-orange-500 to-pink-600 text-white font-semibold rounded-2xl hover:from-orange-600 hover:to-pink-700 focus:outline-none focus:ring-4 focus:ring-orange-300 disabled:opacity-60 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 shadow-lg"
            >
              {loading ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                  Searching...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faSearch} />
                  Track
                </>
              )}
            </button>
          </div>
        </div>

        {/* Result */}
        {loading ? (
          <div className="mt-8 space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-16 bg-gray-100 rounded-xl animate-pulse"></div>
            ))}
          </div>
        ) : trackingData ? (
          <div className="mt-8 p-6 bg-gradient-to-r from-gray-50 to-orange-50 rounded-2xl border border-gray-200">

            {/* Docket + Status */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
              <h4 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                <FontAwesomeIcon icon={faFileAlt} className="text-orange-600" />
                {trackingData.docket}
              </h4>

              {(() => {
                const statusText = (trackingData.docketstatus === 'Completed' || trackingData.booingid?.status === 'Delivered') ? 'Delivered' : (trackingData.booingid?.status || trackingData.docketstatus || 'Initiated');
                const { bg, icon, iconColor } = getStatusStyle(statusText);
                return (
                  <div className={`flex items-center gap-2 px-4 py-2 rounded-full font-semibold text-sm ${bg}`}>
                    <FontAwesomeIcon icon={icon} className={iconColor} />
                    <span>{statusText}</span>
                  </div>
                );
              })()}
            </div>

            {/* Details Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 text-gray-700">

              {(trackingData.bookingdate || trackingData.booingid?.bookingdate) && (
                <div className="flex items-center gap-3">
                  <FontAwesomeIcon icon={faCalendar} className="text-orange-600" />
                  <div>
                    <p className="text-sm text-gray-500">Booking Date</p>
                    <p className="font-semibold">{new Date(trackingData.bookingdate || trackingData.booingid?.bookingdate).toLocaleDateString('en-GB')}</p>
                  </div>
                </div>
              )}

              {(trackingData.consigneeTOaddress || trackingData.consigneeid) && (
                <div className="flex items-center gap-3">
                  <FontAwesomeIcon icon={faUser} className="text-orange-600" />
                  <div>
                    <p className="text-sm text-gray-500">Consignee</p>
                    <p className="font-semibold">{trackingData.consigneeTOaddress || trackingData.consigneeid?.companyName || trackingData.consigneeid?.name || 'N/A'}</p>
                  </div>
                </div>
              )}

              <div className="flex items-center gap-3">
                <FontAwesomeIcon icon={faBox} className="text-orange-600" />
                <div>
                  <p className="text-sm text-gray-500">No of Box</p>
                  <p className="font-semibold">{trackingData.booingid?.noofbox || trackingData.noofpiece || (trackingData.docketmeasurement && trackingData.docketmeasurement[0]?.nos) || 1}</p>
                </div>
              </div>

              {trackingData.actweight != null && (
                <div className="flex items-center gap-3">
                  <FontAwesomeIcon icon={faBox} className="text-orange-600" />
                  <div>
                    <p className="text-sm text-gray-500">Weight (kg)</p>
                    <p className="font-semibold">{trackingData.actweight}</p>
                  </div>
                </div>
              )}

              {trackingData.originlocation && (
                <div className="flex items-center gap-3">
                  <FontAwesomeIcon icon={faMapMarkerAlt} className="text-orange-600" />
                  <div>
                    <p className="text-sm text-gray-500">Origin</p>
                    <p className="font-semibold">{trackingData.originlocation.areaname}, {trackingData.originlocation.hub}</p>
                  </div>
                </div>
              )}

              {trackingData.destlocation && (
                <div className="flex items-center gap-3">
                  <FontAwesomeIcon icon={faMapMarkerAlt} className="text-orange-600" />
                  <div>
                    <p className="text-sm text-gray-500">Destination</p>
                    <p className="font-semibold">{trackingData.destlocation.areaname}, {trackingData.destlocation.hub}</p>
                  </div>
                </div>
              )}

              {trackingData.grandtotal != null && (
                <div className="flex items-center gap-3">
                  <FontAwesomeIcon icon={faBox} className="text-orange-600" />
                  <div>
                    <p className="text-sm text-gray-500">Grand Total</p>
                    <p className="font-semibold">₹{trackingData.grandtotal}</p>
                  </div>
                </div>
              )}

              {/* Consignor */}
              <div className="flex items-center gap-3">
                <FontAwesomeIcon icon={faUser} className="text-orange-600" />
                <div>
                  <p className="text-sm text-gray-500">Consignor</p>
                  <p className="font-semibold">
                    {trackingData.booingid?.pickrequestid?.clientid?.companyName ||
                     trackingData.booingid?.pickrequestid?.clientid?.name ||
                     trackingData.consignorid?.companyName ||
                     trackingData.consignorid?.name || 'N/A'}
                  </p>
                </div>
              </div>

              {/* Mode */}
              <div className="flex items-center gap-3">
                <FontAwesomeIcon icon={faTruck} className="text-orange-600" />
                <div>
                  <p className="text-sm text-gray-500">Mode</p>
                  <p className="font-semibold">{trackingData.booingid?.mode || trackingData.mode || 'Surface'}</p>
                </div>
              </div>
            </div>

            {/* POD Image */}
            {trackingData.file && (
              <div className="mt-6">
                <p className="text-sm font-medium text-gray-600 mb-2 flex items-center gap-2">
                  <FontAwesomeIcon icon={faImage} className="text-orange-600" />
                  Proof of Delivery (POD)
                </p>
                <img
                  src={`${BASE_URL}/${trackingData.file.replace(/\\/g, '/')}`}
                  alt="POD"
                  className="max-w-full rounded-lg shadow-md border border-gray-200"
                  onError={(e) => (e.currentTarget.src = 'https://via.placeholder.com/600x400?text=POD+Not+Found')}
                />
              </div>
            )}
          </div>
        ) : (
          <div className="mt-8 text-center py-12 text-gray-500 bg-white rounded-3xl shadow">
            <FontAwesomeIcon icon={faSearch} className="text-5xl mb-4 text-gray-300" />
            <p className="text-lg">Enter a docket number to track shipment</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Tracking;